"use client";

import { c as _c } from "react/compiler-runtime";
import { getBalanceHealthPhysicianAssigneeForName, getBalanceHealthPhysicianAssigneeForNpi, getBalanceHealthRecallTypeForFaxNumber } from "@/configuration/groups/balance-health/configuration";
import { BH_FAX_LINE_TO_RECALL_TYPE, getBhFaxLineFromNpi } from "@/configuration/groups/balance-health/routing";
import CoverageCard from "@/src/components/cards/coverage-card/coverage-card";
import PatientCard from "@/src/components/cards/patient-card/patient-card";
import ReferralCard from "@/src/components/cards/referral-card/referral-card";
import { ReferralSchedulingRecommendation } from "@/src/components/comms/referral-scheduling-recommendation";
import { CopyableContent } from "@/src/components/copyable-content";
import { EhrInstructionsCard } from "@/src/components/ehr-instructions-card";
import { ReferralProviderCards } from "@/src/components/tasks/referral-provider-cards";
import { formatIsoDate, formatPhoneNumber, formatUSPhoneNumber } from "@/src/lib/utils/date-and-phone-utils";
import { Referral } from "@/src/models/documents/referral";
import { getFullAddress, getPatientName, getPatientPhoneNumber } from "@/src/models/patient";
import { getPersonName } from "@/src/models/person";
const BALANCE_HEALTH_WORKFLOW_TYPE = "Recall";
const BALANCE_HEALTH_DEFAULT_ASSIGNEE = "User, Assort Health";
interface BalanceHealthReferralInstructionsProps {
  referral: Referral;
  faxRecipientNumber: string | null;
  dueDate: string;
  staffLocation: string | null;
  // Whether the group verifies scheduling eligibility on a dedicated task instead of inline here.
  // When false (e.g. Balance Health production, which isn't on the task rollout yet) we keep the
  // inline recommendation/verification controls so those groups don't lose them entirely.
  schedulingVerificationTaskEnabled: boolean;
}

/**
 * Resolves the ModMed recall type for the referral instructions.
 * Priority: receiving provider's NPI → inbound fax line.
 * Falls back to the fax line when the provider NPI is absent or not mapped to a Balance Health
 * practice location (i.e. the provider is not a Balance Health provider).
 */
function getRecallType(referral: Referral, faxRecipientNumber: string | null): string | null {
  const npi = referral.receivingProvider?.npiNumber;
  const providerFaxLine = getBhFaxLineFromNpi(npi);
  if (providerFaxLine !== null) {
    return BH_FAX_LINE_TO_RECALL_TYPE[providerFaxLine];
  }
  if (!faxRecipientNumber) return null;
  try {
    const normalized = formatPhoneNumber(faxRecipientNumber);
    return getBalanceHealthRecallTypeForFaxNumber(normalized);
  } catch {
    return null;
  }
}

// Canonical NPI-keyed roster name (with credential suffix, e.g. ", DPM") wins over a name match,
// which wins over the raw extracted name — same precedence as the CloudCruise assignee resolver
// (`resolveReceivingProvider` in workflow-steps.ts), so the sync box matches what ModMed expects.
function getAssignTo(referral: Referral): string {
  const provider = referral.receivingProvider;
  if (!provider?.familyName) return BALANCE_HEALTH_DEFAULT_ASSIGNEE;
  return getBalanceHealthPhysicianAssigneeForNpi(provider.npiNumber) ?? getBalanceHealthPhysicianAssigneeForName(provider.familyName, provider.givenName) ?? [provider.familyName, provider.givenName].filter(Boolean).join(", ");
}
export function BalanceHealthReferralInstructions(t0) {
  const $ = _c(92);
  const {
    referral,
    faxRecipientNumber,
    dueDate,
    staffLocation,
    schedulingVerificationTaskEnabled
  } = t0;
  let t1;
  if ($[0] !== faxRecipientNumber || $[1] !== referral) {
    t1 = getRecallType(referral, faxRecipientNumber);
    $[0] = faxRecipientNumber;
    $[1] = referral;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const recallType = t1;
  const reason = referral.reasonForReferral ?? null;
  let t2;
  if ($[3] !== referral) {
    t2 = getAssignTo(referral);
    $[3] = referral;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const assignTo = t2;
  let t3;
  if ($[5] !== referral.referringProvider) {
    t3 = referral.referringProvider ? getPersonName(referral.referringProvider) : "";
    $[5] = referral.referringProvider;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const referringProviderName = t3;
  const referringProviderNpi = referral.referringProvider?.npiNumber ?? null;
  const patient = referral.patient;
  let t4;
  if ($[7] !== patient) {
    t4 = patient ? getPatientName(patient) : "";
    $[7] = patient;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const patientName = t4;
  let t5;
  if ($[9] !== patient) {
    t5 = patient?.dateOfBirth ? formatIsoDate(patient.dateOfBirth) : null;
    $[9] = patient;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const patientDob = t5;
  let t6;
  if ($[11] !== patient) {
    const patientPhoneRaw = patient ? getPatientPhoneNumber(patient) : null;
    t6 = patientPhoneRaw ? formatUSPhoneNumber(patientPhoneRaw) : null;
    $[11] = patient;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const patientPhone = t6;
  let t7;
  if ($[13] !== patient) {
    t7 = patient ? getFullAddress(patient) : "";
    $[13] = patient;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const patientAddress = t7;
  let t8;
  let t9;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <span className="font-medium text-center">Balance Health Referral Instructions:</span>;
    t9 = <p className="text-muted-foreground">Use the values below when creating the recall in ModMed. Click any value to copy.</p>;
    $[15] = t8;
    $[16] = t9;
  } else {
    t8 = $[15];
    t9 = $[16];
  }
  let t10;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <span className="font-semibold text-foreground">Referring Provider</span>;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <span className="text-muted-foreground">Name: </span>;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  if ($[19] !== referringProviderName) {
    t12 = <div>{t11}{referringProviderName ? <CopyableContent text={referringProviderName} /> : <span className="text-muted-foreground italic">Not provided</span>}</div>;
    $[19] = referringProviderName;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  let t13;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <span className="text-muted-foreground">NPI: </span>;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  let t14;
  if ($[22] !== referringProviderNpi) {
    t14 = <div>{t13}{referringProviderNpi ? <CopyableContent text={referringProviderNpi} /> : <span className="text-muted-foreground italic">Not provided</span>}</div>;
    $[22] = referringProviderNpi;
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  let t15;
  if ($[24] !== t12 || $[25] !== t14) {
    t15 = <div>{t10}<div className="ml-4 mt-1 flex flex-col gap-1">{t12}{t14}</div></div>;
    $[24] = t12;
    $[25] = t14;
    $[26] = t15;
  } else {
    t15 = $[26];
  }
  let t16;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <span className="font-semibold text-foreground">Patient Demographics</span>;
    $[27] = t16;
  } else {
    t16 = $[27];
  }
  let t17;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <span className="text-muted-foreground">Name: </span>;
    $[28] = t17;
  } else {
    t17 = $[28];
  }
  let t18;
  if ($[29] !== patientName) {
    t18 = <div>{t17}{patientName ? <CopyableContent text={patientName} /> : <span className="text-muted-foreground italic">Not provided</span>}</div>;
    $[29] = patientName;
    $[30] = t18;
  } else {
    t18 = $[30];
  }
  let t19;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <span className="text-muted-foreground">Date of Birth: </span>;
    $[31] = t19;
  } else {
    t19 = $[31];
  }
  let t20;
  if ($[32] !== patientDob) {
    t20 = <div>{t19}{patientDob ? <CopyableContent text={patientDob} /> : <span className="text-muted-foreground italic">Not provided</span>}</div>;
    $[32] = patientDob;
    $[33] = t20;
  } else {
    t20 = $[33];
  }
  let t21;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <span className="text-muted-foreground">Phone: </span>;
    $[34] = t21;
  } else {
    t21 = $[34];
  }
  let t22;
  if ($[35] !== patientPhone) {
    t22 = <div>{t21}{patientPhone ? <CopyableContent text={patientPhone} /> : <span className="text-muted-foreground italic">Not provided</span>}</div>;
    $[35] = patientPhone;
    $[36] = t22;
  } else {
    t22 = $[36];
  }
  let t23;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <span className="text-muted-foreground">Address: </span>;
    $[37] = t23;
  } else {
    t23 = $[37];
  }
  let t24;
  if ($[38] !== patientAddress) {
    t24 = <div>{t23}{patientAddress ? <CopyableContent text={patientAddress} /> : <span className="text-muted-foreground italic">Not provided</span>}</div>;
    $[38] = patientAddress;
    $[39] = t24;
  } else {
    t24 = $[39];
  }
  let t25;
  if ($[40] !== t18 || $[41] !== t20 || $[42] !== t22 || $[43] !== t24) {
    t25 = <div>{t16}<div className="ml-4 mt-1 flex flex-col gap-1">{t18}{t20}{t22}{t24}</div></div>;
    $[40] = t18;
    $[41] = t20;
    $[42] = t22;
    $[43] = t24;
    $[44] = t25;
  } else {
    t25 = $[44];
  }
  let t26;
  let t27;
  if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = <div><span className="text-muted-foreground">Workflow Type: </span><CopyableContent text={BALANCE_HEALTH_WORKFLOW_TYPE} /></div>;
    t27 = <span className="text-muted-foreground">Recall Type: </span>;
    $[45] = t26;
    $[46] = t27;
  } else {
    t26 = $[45];
    t27 = $[46];
  }
  let t28;
  if ($[47] !== recallType) {
    t28 = <div>{t27}{recallType ? <CopyableContent text={recallType} /> : <span className="text-muted-foreground italic">Unknown recall type</span>}</div>;
    $[47] = recallType;
    $[48] = t28;
  } else {
    t28 = $[48];
  }
  let t29;
  if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = <span className="text-muted-foreground">Due Date: </span>;
    $[49] = t29;
  } else {
    t29 = $[49];
  }
  let t30;
  if ($[50] !== dueDate) {
    t30 = <div>{t29}<CopyableContent text={dueDate} /></div>;
    $[50] = dueDate;
    $[51] = t30;
  } else {
    t30 = $[51];
  }
  let t31;
  let t32;
  if ($[52] === Symbol.for("react.memo_cache_sentinel")) {
    t31 = <div><span className="text-muted-foreground">Note: </span><CopyableContent text="Referral from PCP" /></div>;
    t32 = <span className="text-muted-foreground">Reason: </span>;
    $[52] = t31;
    $[53] = t32;
  } else {
    t31 = $[52];
    t32 = $[53];
  }
  let t33;
  if ($[54] !== reason) {
    t33 = <div>{t32}{reason ? <CopyableContent text={reason} /> : <span className="text-muted-foreground italic">Not provided</span>}</div>;
    $[54] = reason;
    $[55] = t33;
  } else {
    t33 = $[55];
  }
  let t34;
  if ($[56] === Symbol.for("react.memo_cache_sentinel")) {
    t34 = <span className="text-muted-foreground">Assign To: </span>;
    $[56] = t34;
  } else {
    t34 = $[56];
  }
  let t35;
  if ($[57] !== assignTo) {
    t35 = <div>{t34}<CopyableContent text={assignTo} /></div>;
    $[57] = assignTo;
    $[58] = t35;
  } else {
    t35 = $[58];
  }
  let t36;
  if ($[59] === Symbol.for("react.memo_cache_sentinel")) {
    t36 = <span className="text-muted-foreground">Staff Location: </span>;
    $[59] = t36;
  } else {
    t36 = $[59];
  }
  let t37;
  if ($[60] !== staffLocation) {
    t37 = <div>{t36}{staffLocation ? <CopyableContent text={staffLocation} /> : <span className="text-muted-foreground italic">Unknown</span>}</div>;
    $[60] = staffLocation;
    $[61] = t37;
  } else {
    t37 = $[61];
  }
  let t38;
  if ($[62] !== t15 || $[63] !== t25 || $[64] !== t28 || $[65] !== t30 || $[66] !== t33 || $[67] !== t35 || $[68] !== t37) {
    t38 = <div className="space-y-3">{t15}{t25}{t26}{t28}{t30}{t31}{t33}{t35}{t37}</div>;
    $[62] = t15;
    $[63] = t25;
    $[64] = t28;
    $[65] = t30;
    $[66] = t33;
    $[67] = t35;
    $[68] = t37;
    $[69] = t38;
  } else {
    t38 = $[69];
  }
  let t39;
  if ($[70] !== referral || $[71] !== schedulingVerificationTaskEnabled) {
    t39 = !schedulingVerificationTaskEnabled && <ReferralSchedulingRecommendation referral={referral} title="Scheduling Recommendation" />;
    $[70] = referral;
    $[71] = schedulingVerificationTaskEnabled;
    $[72] = t39;
  } else {
    t39 = $[72];
  }
  let t40;
  if ($[73] !== t38 || $[74] !== t39) {
    t40 = <EhrInstructionsCard><div className="flex flex-col gap-4 text-sm">{t8}{t9}{t38}{t39}</div></EhrInstructionsCard>;
    $[73] = t38;
    $[74] = t39;
    $[75] = t40;
  } else {
    t40 = $[75];
  }
  let t41;
  if ($[76] !== referral.patient) {
    t41 = <PatientCard patient={referral.patient} />;
    $[76] = referral.patient;
    $[77] = t41;
  } else {
    t41 = $[77];
  }
  let t42;
  let t43;
  if ($[78] !== referral) {
    t42 = <ReferralProviderCards referral={referral} />;
    t43 = <ReferralCard referral={referral} isEditable={true} />;
    $[78] = referral;
    $[79] = t42;
    $[80] = t43;
  } else {
    t42 = $[79];
    t43 = $[80];
  }
  let t44;
  if ($[81] !== referral.primaryCoverage) {
    t44 = referral.primaryCoverage && <CoverageCard coverage={referral.primaryCoverage} type="Primary" isEditable={true} />;
    $[81] = referral.primaryCoverage;
    $[82] = t44;
  } else {
    t44 = $[82];
  }
  let t45;
  if ($[83] !== referral.secondaryCoverage) {
    t45 = referral.secondaryCoverage && <CoverageCard coverage={referral.secondaryCoverage} type="Secondary" isEditable={true} />;
    $[83] = referral.secondaryCoverage;
    $[84] = t45;
  } else {
    t45 = $[84];
  }
  let t46;
  if ($[85] !== t40 || $[86] !== t41 || $[87] !== t42 || $[88] !== t43 || $[89] !== t44 || $[90] !== t45) {
    t46 = <div className="flex flex-col gap-8 w-full sm:min-w-[800px] max-w-[1200px] mx-auto">{t40}{t41}{t42}{t43}{t44}{t45}</div>;
    $[85] = t40;
    $[86] = t41;
    $[87] = t42;
    $[88] = t43;
    $[89] = t44;
    $[90] = t45;
    $[91] = t46;
  } else {
    t46 = $[91];
  }
  return t46;
}